import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { essays } from "@/content/insights";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { year: "numeric", month: "long", day: "numeric" });

const collectionJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "TruthForge Insights",
  url: "https://truthforge.ai/insights",
  description:
    "Long-form analysis on narrative intelligence, frame control, and structural foresight for high-stakes decisions.",
  hasPart: essays.map((e) => ({
    "@type": "Article",
    headline: e.title,
    url: `https://truthforge.ai/insights/${e.slug}`,
    datePublished: e.publishedAt,
  })),
};

const Insights = () => (
  <div className="min-h-screen bg-background text-foreground">
    <SEO
      title="Insights: Narrative Intelligence Analysis | TruthForge AI"
      description="Long-form analysis on narrative collapse, frame control, and structural foresight for legal, executive, and institutional decision-making under pressure."
      path="/insights"
      jsonLd={collectionJsonLd}
    />
    <Navigation />

    <main className="px-5 py-20 md:px-8 lg:py-28">
      <section className="mx-auto max-w-5xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">Insights</p>
        <h1 className="mt-5 text-4xl font-black uppercase leading-tight md:text-6xl">
          Field analysis on how belief forms before exposure.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-body md:text-xl">
          Strategic essays on narrative intelligence, frame control, and the structural mechanics of reputation under pressure.
        </p>

        <div className="mt-16 grid gap-6">
          {essays.map((essay) => (
            <Link
              key={essay.slug}
              to={`/insights/${essay.slug}`}
              className="interface-panel group block p-7 transition hover:border-primary/45 hover:brightness-110 md:p-9"
            >
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] font-bold uppercase tracking-[0.18em] text-body">
                <span className="text-primary">{essay.tag}</span>
                <span>{formatDate(essay.publishedAt)}</span>
                <span>{essay.readMinutes} min read</span>
              </div>
              <h2 className="mt-5 text-2xl font-bold leading-snug text-foreground md:text-3xl">
                {essay.title}
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-relaxed text-body md:text-lg">{essay.dek}</p>
              <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-primary transition group-hover:gap-3">
                Read essay <ArrowRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default Insights;
